import React, { createContext, useContext, useEffect, useState } from 'react';
import firebase, { auth } from './firebaseConfig';
import { API_BASE } from './data/env';

const AuthContext = createContext({ user: null, loading: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firebase.apps.length) {
      setLoading(false);
      return undefined;
    }
    const unsub = auth.onAuthStateChanged(async (u) => {
      // The rules read `email_verified` from the ID token, not from the
      // account. A token minted before the account was verified (link
      // clicked, or the 2026-10-23 backfill) says false for up to an hour
      // and every read is denied — so when the token lags the account,
      // force a refresh before the console renders.
      if (u && !u.isAnonymous && u.emailVerified) {
        try {
          const { claims } = await u.getIdTokenResult();
          if (claims.email_verified !== true) await u.getIdToken(true);
        } catch { /* offline — render with what we have */ }
      }
      setUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function signUp(email, password) {
  return auth.createUserWithEmailAndPassword(email, password);
}

// ---- Verified identity -------------------------------------------------------
// An address counts as verified when Firebase says so (verification link,
// or the one-time backfill) or when the sign-in provider vouches for it:
// Google, Microsoft, or an organization's own SAML/OIDC. Mirrors the rules'
// verifiedEmail() and backend/shared/signupPolicy.js — the rules are the
// boundary; this is what the console gate and the start page show.
const VOUCHING = ['google.com', 'microsoft.com'];
const vouches = (id) => VOUCHING.includes(id) || /^(saml|oidc)\./.test(String(id || ''));
export function isVerified(user) {
  if (!user || user.isAnonymous) return false;
  if (user.emailVerified) return true;
  return (user.providerData || []).some((p) => p && vouches(p.providerId));
}

// Send the verification link; `next` is where the link returns the user
// (same-origin path). The backend mints the code and mails it from
// no-reply@nventr.io through the SES mailer, with the link landing on our
// own /auth/action page (api/authMailApi.js) — Firebase's sender is unused.
export async function sendVerification(user, next = '/console') {
  const target = user || auth.currentUser;
  if (!target) throw new Error('sign in first');
  const token = await target.getIdToken();
  const res = await fetch(`${API_BASE}/auth/verification-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ next: next.startsWith('/') ? next : `/${next}` }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

// After the link is clicked (or a backfill ran) the cached user and ID token
// still say unverified: reload the record and force a token refresh so the
// rules see email_verified on the next request. Returns the fresh verdict.
export async function refreshVerification() {
  const u = auth.currentUser;
  if (!u) return false;
  try { await u.reload(); } catch { /* offline — keep the cached record */ }
  const fresh = auth.currentUser;
  if (fresh && isVerified(fresh)) {
    try { await fresh.getIdToken(true); } catch { /* token refresh is best effort */ }
    return true;
  }
  return false;
}

// Google / Microsoft sign-in: both assert a verified email, so a work
// account arrives verified with no link to click. Redirect flow like SSO
// (popups are blocked in the embedded browsers people sign up from).
export function signInWithGoogle() {
  const provider = new firebase.auth.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return auth.signInWithRedirect(provider);
}
export function signInWithMicrosoft() {
  const provider = new firebase.auth.OAuthProvider('microsoft.com');
  provider.setCustomParameters({ prompt: 'select_account' });
  return auth.signInWithRedirect(provider);
}

export function signIn(email, password) {
  return auth.signInWithEmailAndPassword(email, password);
}

export function signOut() {
  return auth.signOut();
}

// Email password reset — the backend mints the code and mails it from
// no-reply@nventr.io; the link lands on /auth/action and returns the user
// to /login. The route answers the same whether or not the address exists.
export async function sendPasswordReset(email) {
  const res = await fetch(`${API_BASE}/auth/password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || `HTTP ${res.status}`);
  }
}

// Enterprise SSO: providerId is 'saml.<id>' or 'oidc.<id>' as registered by
// Administration → Identity (backend /sso). Redirect flow — the result lands
// in onAuthStateChanged like any other sign-in.
export function signInWithSso(providerId) {
  const provider = providerId.startsWith('saml.')
    ? new firebase.auth.SAMLAuthProvider(providerId)
    : new firebase.auth.OAuthProvider(providerId);
  return auth.signInWithRedirect(provider);
}
